import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { KIT } from "./kit-tokens";

/**
 * C26: "Added to your trip · Undo", a small flat navy note. A dark surface,
 * so it wears no band. It fades itself out after a few seconds (opacity only,
 * so reduced motion loses nothing) and then reports `onDone`.
 */
export function AddedNote({
  onUndo,
  onDone,
  duration = 4000,
}: {
  onUndo?: () => void;
  onDone?: () => void;
  /** How long it stays, in ms. */
  duration?: number;
}) {
  const { t } = useTranslation();
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  // Held in a ref: a new callback each render is not a reason to restart the timer.
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    const out = window.setTimeout(() => setLeaving(true), duration);
    const end = window.setTimeout(() => {
      setGone(true);
      done.current?.();
    }, duration + 220);
    return () => {
      window.clearTimeout(out);
      window.clearTimeout(end);
    };
  }, [duration]);

  if (gone) return null;
  return (
    <div
      role="status"
      data-testid="kit-added-note"
      className="inline-flex items-center gap-2 self-start rounded-full py-1 pe-1 ps-3 text-[13px] text-white transition-opacity duration-200"
      style={{ background: KIT.navy, opacity: leaving ? 0 : 1 }}
    >
      <Check size={14} color={KIT.gold} strokeWidth={3} aria-hidden />
      {t("ai_concierge.kit.added.note")}
      {onUndo ? (
        <button
          type="button"
          onClick={() => {
            setGone(true);
            onUndo();
          }}
          className="min-h-[28px] rounded-full px-3 text-[12px] font-semibold"
          style={{ color: KIT.gold }}
        >
          {t("ai_concierge.kit.added.undo")}
        </button>
      ) : null}
    </div>
  );
}
